import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  DeviceDetail,
  LicenseDto,
  LicenseWithCode,
} from '@sellokart/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AsyncSection } from '@/components/AsyncSection';
import { DeviceStatusBadge, LicenseStatusBadge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TextField } from '@/components/Form';
import { IconBan } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { Skeleton } from '@/components/States';
import { LicenseFlags } from '@/features/licenses/LicenseFlags';
import { LicenseCodeModal } from '@/features/licenses/LicenseCodeModal';
import { useIsOwner } from '@/auth/role';
import { SaSettingsSection } from '@/features/tenants/SaSettingsSection';
import {
  extendLicense,
  issueLicense,
  licenseKeys,
  revokeLicense,
} from '@/features/licenses/api';
import {
  fetchDeviceDetail,
  setDeviceReporting,
  setDeviceSyncInterval,
  tenantKeys,
} from '@/features/tenants/api';
import { formatDateTime, formatRelative } from '@/lib/format';

/** Seconds, matching the stored column — 30s at the fast end, 3h at the slow. */
const SYNC_INTERVAL_OPTIONS = [30, 60, 300, 600, 900, 1800, 3600, 7200, 10800];

function formatSyncInterval(seconds: number): string {
  if (seconds < 60) return `${seconds} sec`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  return hours === 1 ? '1 hour' : `${hours} hours`;
}

/**
 * Everything about one till, on one screen.
 *
 * Licence and device used to live in separate cards on the tenant page, which
 * made the two look like separate things. They are not: an operator asks "is
 * this till licensed, until when, and is it reporting" as one question, and
 * answering it meant matching a licence's typed label against a device list by
 * eye. Here the licence is simply part of the till.
 */
export function DeviceDetailPage() {
  // An ISSUER sees the till and can issue its licence; extend, revoke,
  // reporting and device settings are an owner's (the API refuses them).
  const isOwner = useIsOwner();
  const { id: tenantId, deviceId } = useParams<{ id: string; deviceId: string }>();
  const queryClient = useQueryClient();

  const [extendOpen, setExtendOpen] = useState(false);
  const [extendMonths, setExtendMonths] = useState('12');
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueMonths, setIssueMonths] = useState('12');
  // 'endsOn' carries a migrating customer's existing offline expiry across, so
  // their remaining term is copied rather than re-derived by month arithmetic.
  const [periodMode, setPeriodMode] = useState<'term' | 'endsOn'>('term');
  const [endsOn, setEndsOn] = useState('');
  const [redeemBy, setRedeemBy] = useState('');
  const [issuedCode, setIssuedCode] = useState<LicenseWithCode | null>(null);

  const detailQuery = useQuery({
    queryKey: tenantKeys.device(deviceId ?? ''),
    queryFn: ({ signal }) => fetchDeviceDetail(deviceId!, signal),
    enabled: Boolean(deviceId),
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: tenantKeys.all });
    void queryClient.invalidateQueries({ queryKey: licenseKeys.all });
  }, [queryClient]);

  const reportingMutation = useMutation({
    mutationFn: (enabled: boolean) => setDeviceReporting(deviceId!, enabled),
    onSettled: invalidate,
  });

  const syncIntervalMutation = useMutation({
    mutationFn: (intervalSec: number) => setDeviceSyncInterval(deviceId!, intervalSec),
    onSettled: invalidate,
  });

  const extendMutation = useMutation({
    mutationFn: (license: LicenseDto) =>
      extendLicense(license.id, Number(extendMonths)),
    onSuccess: () => {
      invalidate();
      setExtendOpen(false);
      setExtendMonths('12');
    },
  });

  const revokeLicenseMutation = useMutation({
    mutationFn: (license: LicenseDto) =>
      revokeLicense(license.id, revokeReason.trim()),
    onSuccess: () => {
      invalidate();
      setRevokeOpen(false);
      setRevokeReason('');
    },
  });

  // Issued against THIS till, so activation rebinds to it instead of creating a
  // second device. A hand-registered till had no other route to a licence.
  const issueMutation = useMutation({
    mutationFn: (detail: DeviceDetail) =>
      issueLicense(detail.tenantId, {
        label: detail.label,
        deviceId: detail.id,
        // Exactly one period — sending both would let endsOn silently win and
        // leave the operator unsure which applied.
        ...(periodMode === 'term'
          ? { termMonths: Number(issueMonths) }
          : { endsOn: new Date(`${endsOn}T00:00:00.000Z`).toISOString() }),
        ...(redeemBy
          ? { redeemBy: new Date(`${redeemBy}T23:59:59.000Z`).toISOString() }
          : {}),
      }),
    onSuccess: (result) => {
      invalidate();
      setIssueOpen(false);
      setIssuedCode(result);
    },
  });

  return (
    <>
      <AsyncSection
        state={detailQuery}
        skeleton={<Skeleton className="h-64 w-full" />}
        errorTitle="Could not load this till"
      >
        {(detail: DeviceDetail) => {
          const license = detail.license;
          const disconnected = detail.revokedAt !== null;

          return (
            <>
              <PageHeader
                eyebrow={
                  <Link
                    to={`/cp/tenants/${tenantId}`}
                    className="rounded text-sm text-slate-600 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
                  >
                    ← {detail.tenantName}
                  </Link>
                }
                title={detail.label}
                meta={
                  <DeviceStatusBadge
                    revokedAt={detail.revokedAt}
                    lastSeenAt={detail.lastSeenAt}
                  />
                }
                description={`${detail.branchName} · registered ${formatDateTime(detail.createdAt)}`}
              />

              <div className="space-y-4">
                <SectionCard
                  headingId="device-licence"
                  title="Licence"
                  description="What lets this till trade. Independent of cloud reporting."
                  action={
                    // `license` holds only a LIVE licence — revoked ones move to
                    // history — so no licence here means the till cannot trade,
                    // whether it never could or its licence was revoked. Either
                    // way the way out is the same, and offering it is what stops
                    // revoking being a dead end.
                    !license ? (
                      <Button size="sm" variant="primary" onClick={() => setIssueOpen(true)}>
                        {detail.licenseHistory.length > 0
                          ? 'Issue new licence'
                          : 'Issue licence'}
                      </Button>
                    ) : !isOwner ? undefined : (
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setExtendOpen(true)}>
                          Extend
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          icon={<IconBan className="h-3.5 w-3.5" />}
                          onClick={() => setRevokeOpen(true)}
                          title="Stops this till TRADING at its next check-in. To only stop it uploading, turn reporting off instead."
                        >
                          Revoke licence
                        </Button>
                      </div>
                    )
                  }
                >
                  <div className="space-y-3 px-5 py-4">
                    {license ? (
                      <>
                        <dl className="grid gap-4 sm:grid-cols-3">
                          <Field label="Status">
                            <LicenseStatusBadge license={license} />
                          </Field>
                          <Field label="Valid to">
                            {license.expiresAt
                              ? formatDateTime(license.expiresAt)
                              : license.redeemBy
                                ? `redeem by ${formatDateTime(license.redeemBy)}`
                                : '—'}
                          </Field>
                          <Field label="Activation code">
                            <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                              {license.codePrefix}…
                            </code>
                          </Field>
                          <Field label="Last licence check">
                            {license.status === 'PENDING'
                              ? 'Never'
                              : formatRelative(license.lastRefreshAt)}
                          </Field>
                          <Field label="Machine">
                            {license.hostname ?? license.machineId ?? '—'}
                          </Field>
                          <Field label="POS version">
                            {license.posVersion ?? '—'}
                          </Field>
                        </dl>

                        {license.revokedReason ? (
                          <p className="text-sm text-rose-700">
                            Revoked: {license.revokedReason}
                          </p>
                        ) : null}

                        <LicenseFlags license={license} />
                      </>
                    ) : (
                      <p className="text-sm text-slate-600">
                        {detail.licenseHistory.length > 0
                          ? 'This till has no live licence, so it cannot trade. Issue a new one — the code binds to this till, so its reporting history stays one stream.'
                          : 'No licence is attached to this till. It was registered by hand, so it can sync but has nothing to trade on. Issue one here — the code will bind to this till, so activating it will not create a second device.'}
                      </p>
                    )}
                  </div>
                </SectionCard>

                {isOwner ? (
                <SectionCard
                  headingId="device-reporting"
                  title="Cloud reporting"
                  description="Whether THIS till uploads sales data. Separate from its licence."
                >
                  <div className="space-y-3 px-5 py-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={detail.reportingEnabled}
                          aria-label={`Cloud reporting for ${detail.label}`}
                          disabled={reportingMutation.isPending || disconnected}
                          onClick={() =>
                            reportingMutation.mutate(!detail.reportingEnabled)
                          }
                          className={`inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                            detail.reportingEnabled ? 'bg-brand-700' : 'bg-slate-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                              detail.reportingEnabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <p className="text-sm text-slate-700">
                          {detail.reportingEnabled
                            ? 'On — this till uploads sales data. It picks changes up within the hour, or immediately on restart.'
                            : 'Off — this till keeps every report locally and sends nothing. It still checks in about its licence.'}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <label htmlFor="sync-interval" className="text-sm font-medium text-slate-700">
                          Push every
                        </label>
                        <select
                          id="sync-interval"
                          value={detail.syncIntervalSec}
                          disabled={
                            !detail.reportingEnabled || disconnected || syncIntervalMutation.isPending
                          }
                          onChange={(event) =>
                            syncIntervalMutation.mutate(Number(event.target.value))
                          }
                          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {SYNC_INTERVAL_OPTIONS.map((seconds) => (
                            <option key={seconds} value={seconds}>
                              {formatSyncInterval(seconds)}
                            </option>
                          ))}
                        </select>
                        <p className="text-sm text-slate-500">
                          Console-owned — the POS has no control of its own, it shows this value read-only.
                        </p>
                      </div>
                  </div>
                </SectionCard>
                ) : null}

                {detail.licenseHistory.length > 0 ? (
                  <SectionCard
                    headingId="device-licence-history"
                    title="Licence history"
                    count={detail.licenseHistory.length}
                    description="Earlier licences for this till, newest first. Kept so a revoked term explains itself."
                  >
                    <ul className="divide-y divide-slate-200">
                      {detail.licenseHistory.map((old) => (
                        <li key={old.id} className="px-5 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <LicenseStatusBadge license={old} />
                            <span className="font-mono text-xs text-slate-500">
                              {old.codePrefix}…
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-slate-600">
                            Issued {formatDateTime(old.createdAt)}
                            {old.activatedAt
                              ? `, activated ${formatDateTime(old.activatedAt)}`
                              : ', never activated'}
                            {old.revokedAt
                              ? `, revoked ${formatDateTime(old.revokedAt)}`
                              : null}
                          </p>
                          {old.revokedReason ? (
                            <p className="mt-0.5 text-sm text-rose-700">
                              Reason: {old.revokedReason}
                            </p>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </SectionCard>
                ) : null}

                {isOwner ? (
                  <SaSettingsSection
                    deviceId={detail.id}
                    deviceLabel={detail.label}
                    disconnected={disconnected}
                  />
                ) : null}

                <SectionCard
                  headingId="device-sync"
                  title="Sync connection"
                  description="How this till authenticates when it uploads. Issued by activation, so there is nothing to manage here."
                >
                  <div className="px-5 py-4">
                    <dl className="grid gap-4 sm:grid-cols-3">
                      <Field label="Sync token">
                        <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                          {detail.tokenPrefix}…
                        </code>
                      </Field>
                      <Field label="Last sync">
                        {formatRelative(detail.lastSeenAt, 'Never synced')}
                      </Field>
                      <Field label="Connection">
                        <DeviceStatusBadge
                          revokedAt={detail.revokedAt}
                          lastSeenAt={detail.lastSeenAt}
                        />
                      </Field>
                    </dl>
                  </div>
                </SectionCard>
              </div>

              {/* ---- dialogs ---- */}


              <ConfirmDialog
                open={extendOpen}
                title="Extend this licence"
                confirmLabel="Extend"
                pending={extendMutation.isPending}
                error={extendMutation.isError ? extendMutation.error : undefined}
                onClose={() => setExtendOpen(false)}
                onConfirm={() => license && extendMutation.mutate(license)}
              >
                <p className="mb-3">
                  Months are added to the existing expiry, not to today, so no
                  paid days are lost.
                </p>
                <TextField
                  id="extend-months"
                  label="Months"
                  type="number"
                  min={1}
                  value={extendMonths}
                  onChange={(event) => setExtendMonths(event.target.value)}
                />
              </ConfirmDialog>

              <ConfirmDialog
                open={revokeOpen}
                title="Revoke this licence"
                confirmLabel="Revoke licence"
                confirmVariant="danger"
                pending={revokeLicenseMutation.isPending}
                error={
                  revokeLicenseMutation.isError
                    ? revokeLicenseMutation.error
                    : undefined
                }
                onClose={() => setRevokeOpen(false)}
                onConfirm={() => license && revokeLicenseMutation.mutate(license)}
              >
                <p className="mb-3">
                  <span className="font-medium text-slate-900">{detail.label}</span>{' '}
                  will STOP TRADING at its next check-in. This is not the same as
                  disconnecting it from the cloud.
                </p>
                <TextField
                  id="revoke-reason"
                  label="Reason"
                  value={revokeReason}
                  onChange={(event) => setRevokeReason(event.target.value)}
                  placeholder="e.g. account closed"
                />
              </ConfirmDialog>
              <ConfirmDialog
                open={issueOpen}
                title="Issue a licence for this till"
                confirmLabel="Issue licence"
                pending={issueMutation.isPending}
                error={issueMutation.isError ? issueMutation.error : undefined}
                onClose={() => setIssueOpen(false)}
                onConfirm={() => {
                  if (periodMode === 'endsOn' && !endsOn) return;
                  issueMutation.mutate(detail);
                }}
              >
                <p className="mb-4">
                  The code is bound to{' '}
                  <span className="font-medium text-slate-900">{detail.label}</span>{' '}
                  on{' '}
                  <span className="font-medium text-slate-900">
                    {detail.branchName}
                  </span>
                  , so redeeming it re-uses this till rather than registering a
                  new one. The code is shown once.
                </p>

                <fieldset className="mb-4 space-y-3">
                  <legend className="label">Licence period</legend>

                  <label className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="device-period"
                      className="mt-1"
                      checked={periodMode === 'term'}
                      onChange={() => setPeriodMode('term')}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-slate-800">
                        Term in months
                      </span>
                      <TextField
                        id="issue-months"
                        label="Months"
                        type="number"
                        min={1}
                        max={1200}
                        value={issueMonths}
                        disabled={periodMode !== 'term'}
                        hint="Counted from activation, not from today."
                        onChange={(event) => setIssueMonths(event.target.value)}
                      />
                    </span>
                  </label>

                  <label className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="device-period"
                      className="mt-1"
                      checked={periodMode === 'endsOn'}
                      onChange={() => setPeriodMode('endsOn')}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-slate-800">Ends on</span>
                      <TextField
                        id="issue-ends-on"
                        label="Expiry date"
                        type="date"
                        value={endsOn}
                        disabled={periodMode !== 'endsOn'}
                        hint="Use when moving a customer off an offline key — copy their existing expiry so the remaining term carries across."
                        onChange={(event) => setEndsOn(event.target.value)}
                      />
                    </span>
                  </label>
                </fieldset>

                <TextField
                  id="issue-redeem-by"
                  label="Redeem by (optional)"
                  type="date"
                  value={redeemBy}
                  hint="Deadline to activate the code. Defaults to 30 days."
                  onChange={(event) => setRedeemBy(event.target.value)}
                />
              </ConfirmDialog>
            </>
          );
        }}
      </AsyncSection>

      <LicenseCodeModal
        issued={issuedCode}
        onClose={() => setIssuedCode(null)}
      />

    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-800">{children}</dd>
    </div>
  );
}
